import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Typography } from "@mui/material";
import {
  Chip,
  ConfirmDialog,
  Drawer,
  StatusGlyph,
  Timeline,
} from "@outpave/ds";
import Summary, { SummaryItem } from "components/common/Summary";
import { Inline, Stack } from "components/layout/reusableLayouts";
import {
  DELIVERY_STATUS_LABEL,
  DELIVERY_STATUS_TONE,
  DELIVERY_TRIGGER_LABEL,
  formatInstant,
  getAttemptSteps,
  getStatusReasonLabel,
  isReplayable,
  type WebhookDelivery,
  type WebhookDeliveryStatus,
} from "./webhookDeliveriesUtils";

type WebhookDeliveryDrawerProps = {
  delivery: WebhookDelivery | null;
  open: boolean;
  replaying: boolean;
  onClose: () => void;
  onAfterClose: () => void;
  onReplay: (delivery: WebhookDelivery) => Promise<void>;
};

export const WebhookDeliveryStatusChip: React.FC<{
  status: WebhookDeliveryStatus;
}> = ({ status }) => {
  const { t } = useTranslation();
  const tone = DELIVERY_STATUS_TONE[status];
  return (
    <Chip
      size="sm"
      tone={tone}
      leading={
        <StatusGlyph status={DELIVERY_STATUS_LABEL[status]} tone={tone} />
      }
    >
      {t(DELIVERY_STATUS_LABEL[status])}
    </Chip>
  );
};

const WebhookDeliveryDrawer: React.FC<WebhookDeliveryDrawerProps> = ({
  delivery,
  open,
  replaying,
  onClose,
  onAfterClose,
  onReplay,
}) => {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);

  const items: SummaryItem[] = delivery
    ? [
        { title: "Event", value: delivery.eventType ?? "-" },
        {
          title: "Trigger",
          value: t(DELIVERY_TRIGGER_LABEL[delivery.trigger]),
        },
        {
          title: "Reason",
          value:
            delivery.statusReason &&
            getStatusReasonLabel(delivery.statusReason, t),
          fullWidth: true,
        },
        { title: "Created", value: formatInstant(delivery.createdAt) },
        { title: "Attempts", value: String(delivery.attemptCount) },
        {
          title: "Next attempt",
          value:
            delivery.nextAttemptAt && formatInstant(delivery.nextAttemptAt),
        },
        {
          title: "Completed",
          value: delivery.completedAt && formatInstant(delivery.completedAt),
        },
        { title: "Event ID", value: delivery.eventId, fullWidth: true },
        { title: "Delivery ID", value: delivery.id, fullWidth: true },
      ]
    : [];
  const steps = delivery ? getAttemptSteps(delivery, t) : [];

  const handleConfirm = async () => {
    if (delivery) await onReplay(delivery);
    setConfirming(false);
  };

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        onAfterClose={onAfterClose}
        title={[t("Webhook delivery")]}
        headerAction={
          delivery && <WebhookDeliveryStatusChip status={delivery.status} />
        }
        footer={
          <Inline gap="var(--space-3)" justify="end">
            <Button variant="default-outlined" onClick={onClose}>
              {t("Exit")}
            </Button>
            {delivery && isReplayable(delivery) && (
              <Button
                variant="primary-filled"
                onClick={() => setConfirming(true)}
                disabled={replaying}
              >
                {t("Replay")}
              </Button>
            )}
          </Inline>
        }
      >
        <Stack gap="var(--space-5)">
          <Summary items={items} disableContainer />
          <Stack gap="var(--space-3)">
            <Typography variant="overline" color="text.secondary">
              {t("Attempts")}
            </Typography>
            {steps.length > 0 ? (
              <Timeline steps={steps} />
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t("No attempts yet")}
              </Typography>
            )}
          </Stack>
        </Stack>
      </Drawer>
      <ConfirmDialog
        open={confirming}
        title={t("Replay this event?")}
        message={t("ReplayEventMessage")}
        confirmLabel={t("Replay")}
        cancelLabel={t("Cancel")}
        tone="primary"
        busy={replaying}
        onConfirm={handleConfirm}
        onCancel={() => setConfirming(false)}
      />
    </>
  );
};

export default WebhookDeliveryDrawer;
